import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner';
import { SendToUsersDto, SendToGroupDto, NotificationType, NotificationPriority } from '../../../api/notificationApi';

interface SendNotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  templates: any[];
  groups: any[];
  users: any[];
  selectedUsers: string[];
  selectedGroups: string[];
  onSendToUsers: (dto: SendToUsersDto) => Promise<any>;
  onSendToGroups: (dto: SendToGroupDto) => Promise<any>;
}

export default function SendNotificationModal({ 
  isOpen, 
  onClose, 
  templates, 
  groups, 
  users,
  selectedUsers,
  selectedGroups,
  onSendToUsers,
  onSendToGroups
}: SendNotificationModalProps) {
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    type: 'SYSTEM' as NotificationType,
    priority: 'NORMAL' as NotificationPriority,
    templateId: '',
    targetType: 'users' as 'users' | 'group',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Automatically set target type based on what's selected
  useEffect(() => {
    if (selectedUsers.length > 0 && selectedGroups.length === 0) {
      setFormData(prev => ({ ...prev, targetType: 'users' }));
    } else if (selectedGroups.length > 0 && selectedUsers.length === 0) {
      setFormData(prev => ({ ...prev, targetType: 'group' }));
    }
  }, [selectedUsers.length, selectedGroups.length]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.body.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Validate based on what's actually selected
    if (selectedUsers.length === 0 && selectedGroups.length === 0) {
      toast.error('Please select at least one user or group');
      return;
    }

    // Ensure target type matches what's selected
    if (selectedUsers.length > 0 && selectedGroups.length === 0) {
      formData.targetType = 'users';
    } else if (selectedGroups.length > 0 && selectedUsers.length === 0) {
      formData.targetType = 'group';
    }

    setIsSubmitting(true);
    try {
      console.log(formData)
      if (formData.targetType === 'users') {
        console.log("ewihfebw")
        const dto: SendToUsersDto = {
          userIds: selectedUsers,
          subject: formData.subject,
          body: formData.body,
          type: formData.type,
          priority: formData.priority,
          templateId: formData.templateId && formData.templateId !== 'no-template' ? formData.templateId : undefined,
        };
        await onSendToUsers(dto);
      } else {
        const dto: SendToGroupDto = {
          groupId: selectedGroups[0], // Use first selected group
          subject: formData.subject,
          body: formData.body,
          type: formData.type,
          priority: formData.priority,
          templateId: formData.templateId && formData.templateId !== 'no-template' ? formData.templateId : undefined,
        };
        await onSendToGroups(dto);
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast.error('Failed to send notification');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      subject: '',
      body: '',
      type: NotificationType.SYSTEM,
      priority: NotificationPriority.NORMAL,
      templateId: '',
      targetType: selectedUsers.length > 0 ? 'users' : 'group',
    });
    onClose();
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleTemplateSelect = (templateId: string) => {
    if (templateId === 'no-template') {
      setFormData(prev => ({
        ...prev,
        subject: '',
        body: '',
        type: NotificationType.SYSTEM,
        templateId: '',
      }));
    } else if (templateId) {
      const template = templates.find(t => t.id === templateId);
      if (template) {
        setFormData(prev => ({
          ...prev,
          subject: template.subject,
          body: template.bodyTemplate,
          type: template.type,
          templateId,
        }));
      }
    }
  };



  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Send Notification</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SYSTEM">System</SelectItem>
                  <SelectItem value="COURSE">Course</SelectItem>
                  <SelectItem value="ADMIN">Admin</SelectItem>
                  <SelectItem value="WELCOME">Welcome</SelectItem>
                  <SelectItem value="REMINDER">Reminder</SelectItem>
                  <SelectItem value="ALERT">Alert</SelectItem>
                  <SelectItem value="CUSTOM">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="priority">Priority</Label>
              <Select value={formData.priority} onValueChange={(value) => handleInputChange('priority', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LOW">Low</SelectItem>
                  <SelectItem value="NORMAL">Normal</SelectItem>
                  <SelectItem value="HIGH">High</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="template">Use Template (Optional)</Label>
            <Select value={formData.templateId} onValueChange={handleTemplateSelect}>
              <SelectTrigger>
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="no-template">No Template</SelectItem>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject *</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter notification subject"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="body">Message Body *</Label>
            <Textarea
              id="body"
              value={formData.body}
              onChange={(e) => handleInputChange('body', e.target.value)}
              placeholder="Enter notification message"
              rows={4}
              required
            />
          </div>

          <div className="bg-[var(--primary-font)] border border-blue-200 rounded-lg p-3">
            <div className="text-sm font-medium text-white mb-1">
              {selectedUsers.length > 0 ? 'Target Users' : 'Target Groups'}
            </div>
            <div className="text-xs text-white">
              {selectedUsers.length > 0 
                ? `${selectedUsers.length} user(s) selected` 
                : `${selectedGroups.length} group(s) selected`
              }
            </div>
            {selectedUsers.length > 0 && (
              <div className="text-xs text-white mt-1">
                Users: {users.filter(u => selectedUsers.includes(u.id)).map(u => u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown').join(', ')}
              </div>
            )}
            {selectedGroups.length > 0 && (
              <div className="text-xs text-white mt-1">
                Groups: {groups.filter(g => selectedGroups.includes(g.id)).map(g => g.name).join(', ')}
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Sending...' : 'Send Notification'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 